import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  InputGroup,
  Text,
} from "@chakra-ui/react";
import useApi from "hooks/useApi";
import { HttpStatusCode } from "axios";
import {
  SORT_TYPES,
  SORT_DIRECTIONS,
} from "constants/filterDropdownData";
import Pagination from "components/pagination/Pagination";
import ErrorDisplay from "components/errorDisplay/ErrorDisplay";
import { SearchBar } from "components/navbar/searchBar/SearchBar";
import { showToast } from "components/toast/Toast";
import { OverlayLoaderComponent } from "components/loader/Loader";
import Menu from "components/menu/MainMenu";
import SidebarFilter from "components/sidebarFilter/SidebarFilter";
import { getFilterFromLocalStorage, setFilterToLocalStorage } from "utils/common";
import { ENTITY_FILTER_TYPES, ENTITY_COLUMNS_TYPES } from "utils/common";
import ManageColumns from "components/manageColumns/ManageColumns";
import NoDataFound from "components/noDataFound/NoDataFound";
import { FiChevronsDown, FiFilter } from "react-icons/fi";
import CustomTable from "components/table/CustomTable";
import { useLocation } from "react-router-dom";
import { SUBSCRIPTIONS_API, TEAM_MEMBERS_API, ASSIGN_SUBSCRIPTION_SALES_PERSON_API_FN } from "constants/endpoints";
import { subscriptionColumns } from "./variables";
import { SUBSCRIPTION_STATUS } from "constants/filterDropdownData";
import { formatSubscriptionsTableData } from "utils/common";
import { formatDateToMMDDYYYY } from "utils/common";
import { SUB_STATUS } from "constants/filterDropdownData";
import AddPropertyModal from "./modal/AddPropertyModal";
import { PROPERTIES_LISTING_API } from "constants/endpoints";
import { ADD_PROPERTIES_API } from "constants/endpoints";
import { useAuth } from "contexts/AuthContext";
import { useQueryParams } from "utils/common";
import { getUniquePlanDropdownData } from "constants/commonFunctions";
import { useHistory } from "react-router-dom";
import clearCaching from "utils/clearCaching";
import { SUBSCRIPTION_CREATED_BY } from "constants/filterDropdownData";

const PAGE_SIZE = 50;
const sortableColumns = ["start_date", "end_date", "created_at"];

export default function Subscriptions() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [properties, setProperties] = useState([]);
  const [subscriptionData, setSubscriptionData] = useState([]);
  const { apiCall, loading = true, error } = useApi();
  const [selectedAction, setSelectedAction] = useState("");
  const [isOpenFilterSidebar, setIsOpenFilterSidebar] = useState(false);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const lsFilters = getFilterFromLocalStorage(ENTITY_FILTER_TYPES.SUBSCRIPTION_FILTER);
  const [totalPages, setTotalPages] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [manageColumnSelectḀ̥̥̥̥̥̥̥̥llCheckbox, setManageColumnSelectAllCheckbox] = useState(false);
  const { ord_id } = useQueryParams();
  const history = useHistory();
  const lsUsersDateRange = getFilterFromLocalStorage(
    ENTITY_FILTER_TYPES.SUBSCRIPTION_DATE_RANGE
  );
  const lsFiltersUsersPageSize = getFilterFromLocalStorage(
    ENTITY_FILTER_TYPES.USERS_SUBSCRIPTION_PAGE_SIZE
  );
  const { plans} = useAuth();
  const freemiumPlan = plans.filter(plan => plan.type === "freemium");
  const initialFiltersData = {
    plan: [],
    status: "",
    freemium_plan_id: freemiumPlan[0]?._id,
    dateRange: {
      startDate: '',
      endDate: '',
    },
  };

  const [filters, setFilters] = useState(lsFilters ? lsFilters : { ...initialFiltersData });
  const {
    plan = [],
    freemium_plan_id = freemiumPlan[0]?._id,
    status = "",
    dateRange: { startDate = "", endDate = "" } = {},
  } = filters || {};
  const [filteredData, setFilteredData] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    sortType: "created_at",
    sortDirection: "descending",
  });
  const [selectedRowsData, setSelectedRowsData] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const { search } = useLocation();
  const queryObject = Object.fromEntries(new URLSearchParams(search));
  const PLAN_DROPDOWN_DATA = getUniquePlanDropdownData(plans);

  const fetchSubscriptionsData = useCallback(async (searchTextParam = searchText) => {
    try {
      const body = {
        search_text: searchTextParam,
        page: currentPage,
        limit: pageSize,
        status,
        plan:plan?.join(','),
        freemium_plan_id,
        start_date: formatDateToMMDDYYYY(startDate),
        end_date: formatDateToMMDDYYYY(endDate),
        created_by: filters?.created_by?.join(","),
        sales_person: filters?.sales_person?.join(","),
      };
      const res = await apiCall.get(SUBSCRIPTIONS_API, body);
      if (res?.statusCode === HttpStatusCode.Ok) {
        setSubscriptionData(res?.data?.subscriptions);
        setTotalPages(Math.ceil(res?.data?.count / pageSize));
        setTotalCount(res?.data?.count);
      }
    } catch (error) {
      console.log("Error while fetching subscriptions: ", error.message);
    }
  }, [currentPage, pageSize, searchText, filters]);

  const toggleSidebar = () => {
    setIsOpenFilterSidebar(!isOpenFilterSidebar);
  };

  const handleFilterChange = useCallback((key, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [key]: value,
    }));
    setCurrentPage(1);
  }, []);

  const handleApplyFilter = () => {
    const applyFilterArray = Object.keys(filters).filter((key) => filters[key]);
    setFilteredData(applyFilterArray);
    const filtersWithDateRange = {
      ...filters,
      dateRange: {
        startDate: startDate,
        endDate: endDate,
      },
    };
    setFilterToLocalStorage(ENTITY_FILTER_TYPES.SUBSCRIPTION_FILTER, filtersWithDateRange);
    setCurrentPage(1);
    setSelectedRowsData([]);
    fetchSubscriptionsData();
  };

  const handleRemoveAllFilter = () => {
    setFilteredData([]);
    setFilters({
      ...initialFiltersData,
      dateRange: {
        startDate: "",
        endDate: "",
      },
    });
  };

  const SALES_PERSON_DROPDOWN_DATA = teamMembers.map((m) => ({
    label: `${m.first_name || ''} ${m.last_name || ''}`.trim() || m.email || m._id,
    value: m._id,
  }));

  const dropdownFilters = [
    {
      name: "status",
      placeholder: "Status",
      value: filters?.status,
      data: SUBSCRIPTION_STATUS,
    },
    {
      name: 'plan',
      data: PLAN_DROPDOWN_DATA,
      placeholder: 'Select Plan',
      value: plan,
    },
    {
      name: 'created_by',
      placeholder: 'Created By',
      value: filters?.created_by,
      data: SUBSCRIPTION_CREATED_BY,
    },
    {
      name: 'sales_person',
      placeholder: 'Sales Person',
      value: filters?.sales_person,
      data: SALES_PERSON_DROPDOWN_DATA,
    },
  ];

  const dropdownSorts = [
    {
      name: "sortType",
      placeholder: "",
      value: sortConfig.sortType,
      data: SORT_TYPES,
    },
    {
      name: "sortDirection",
      placeholder: "",
      value: sortConfig.sortDirection,
      data: SORT_DIRECTIONS,
    },
  ];

  const allFiltersProps = {
    dropdowns: dropdownFilters,
    sorts: dropdownSorts,
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await apiCall.get(TEAM_MEMBERS_API);
        const list = Array.isArray(res?.data) ? res.data : (res?.data?.data || []);
        setTeamMembers(list);
      } catch (err) {
        console.log('team-members fetch failed', err);
      }
    })();
  }, []);

  const handleAssignSalesPerson = useCallback(async (subscriptionId, salesPersonId) => {
    try {
      await apiCall.patch(ASSIGN_SUBSCRIPTION_SALES_PERSON_API_FN(subscriptionId), {
        sales_person_id: salesPersonId,
      });
      showToast({
        message: salesPersonId ? "Sales person assigned" : "Sales person unassigned",
        customStyle: { background: "green", color: "#fff", padding: "10px" },
      });
      fetchSubscriptionsData();
    } catch (err) {
      showToast({
        message: err?.response?.data?.error?.message || "Failed to update sales person",
        customStyle: { background: "#ff4d4f", color: "#fff", padding: "10px" },
      });
    }
  }, [apiCall, fetchSubscriptionsData]);

  const columns = useMemo(
    () => subscriptionColumns({ teamMembers, onAssignSalesPerson: handleAssignSalesPerson }),
    [teamMembers, handleAssignSalesPerson]
  );
  const data = useMemo(() => formatSubscriptionsTableData(subscriptionData), [subscriptionData]);
  const excludedColumnVisibility = ["user_email"];
  const [columnVisibility, setColumnVisibility] = useState(
    columns.reduce((acc, column) => {
      acc[column.accessor] = !excludedColumnVisibility.includes(column.accessor);
      return acc;
    }, {})
  );

  const handleColumnChange = (accessor) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [accessor]: !prev[accessor],
    }));
  };

  useEffect(() => {
    setFilterToLocalStorage(ENTITY_COLUMNS_TYPES.SUBSCRIPTIONS_COLUMNS, columnVisibility);
  }, [columnVisibility]);

  useEffect(() => {
    if (ord_id) {
      fetchSubscriptionsData(ord_id);
    } else if (queryObject?.search_text) {
      setSearchText(queryObject?.search_text);
      fetchSubscriptionsData(queryObject?.search_text);
    } else {
      setSearchText("");
      fetchSubscriptionsData();
    }
  }, [ord_id, queryObject?.search_text, pageSize, currentPage, sortConfig]);

  const visibleColumns = columns.filter((column) => columnVisibility[column.accessor]);

  const handleInputFormSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchSubscriptionsData();
  };

  const handleRowSelect = useCallback((row) => {
    const findData = selectedRowsData.find((item) => item._id === row._id);
    setSelectedRowsData((prev) => {
      if (findData) {
        return prev.filter((item) => item._id !== findData._id);
      } else {
        return [...prev, row];
      }
    });
  }, [selectedRowsData]);

  const handleAddProperty = async (phone, _id) => {
    try {
      const res = await apiCall.get(`${PROPERTIES_LISTING_API}?search_text=${phone}`);
      if (res) {
        setProperties(res.data.data);
        setIsModalOpen(true);
      }
    } catch (error) {
      console.error("Error fetching properties:", error.message);
      showToast("Error fetching properties", "error");
    }
  };

const handleSubmitProperty = async (selectedProperty) => {
  try {
    if (!selectedProperty) {
      showToast({
        message: "No property selected",
        success: false,
        customStyle: {
          background: '#F24052',
          color: '#fff',
          padding: '10px',
        },
      });
      return;
    }

    const body = {
      ad_id: selectedProperty._id,
      ad_ref: selectedProperty.collection_type,
      subscription_id: selectedAction?._id,
    };

    const res = await apiCall.patch(ADD_PROPERTIES_API, body);

    if (res?.statusCode === HttpStatusCode.Ok) {
      await clearCaching({
        tags: [`getPropertyDetailsData_${selectedProperty?.slug_url}`]
      })

      showToast({
        message: 'Property added successfully',
        success: true,
        customStyle: {
          background: '#52c41a',
          color: '#fff',
          padding: '10px',
        },
      });
    }
  } catch (error) {
    console.error("Error adding property:", error);
    const errorMessage = error?.response?.data?.message || "Error adding property";
    fetchSubscriptionsData();
    showToast({
      message: `Error: ${errorMessage}`,
      success: false,
      customStyle: {
        background: '#F24052',
        color: '#fff',
        padding: '10px',
      },
    });
  }
};

async function handleMenuOptionClick({status, subscription_id}){
  if(status === "Edit"){
    history.push(`/admin/subscription?id=${subscription_id}`)
  }
}

  if (error) {
    return <ErrorDisplay error={error} />;
  }

  return (
    <>
      <Box height="100vh" bg="#F8FAFB">
        <Flex
          justify="space-between"
          p={{ base: 2, md: 4 }}
          align="center"
          direction={{ base: "column", md: "row" }}
          boxShadow="sm"
          borderRadius="sm"
          pos={{ base: "relative", md: "fixed" }}
          w={{ base: "100%", md: "calc(100vw - 96px)" }}
          zIndex={1000}
          gap={{ base: 3, md: 4 }}
        >
          <Flex
            gap={{ base: 2, md: 4 }}
            w={{ base: 'full', md: "auto" }}
            direction={{ base: "column", md: "row" }}
            p={{ base: 1, md: 2 }}
          >
            <Flex
              gap={{ base: 2, md: 4 }}
              align="center"
              justify={{ base: "end", md: "space-between" }}
              w={{ base: 'full', md: "auto" }}
              wrap="wrap"
            >
            <Box position="relative" display="inline-block">
              <Button
                paddingX={6}
                leftIcon={<FiFilter />}
                rightIcon={<FiChevronsDown />}
                colorScheme="teal"
                variant="outline"
                borderRadius="md"
                size="sm"
                onClick={toggleSidebar}
              >
                Filter
              </Button>
              {filteredData.length > 0 && (
                <Badge
                  position="absolute"
                  top="-3"
                  right="0"
                  colorScheme="teal"
                  borderRadius="full"
                  px={2}
                  fontSize="0.8em"
                >
                  {filteredData.length}
                </Badge>
              )}
            </Box>
            <ManageColumns
              columns={columns}
              handleColumnChange={handleColumnChange}
              columnVisibility={columnVisibility}
              setManageColumnSelectAllCheckbox={setManageColumnSelectAllCheckbox}
              manageColumnSelectḀ̥̥̥̥̥̥̥̥llCheckbox={manageColumnSelectḀ̥̥̥̥̥̥̥̥llCheckbox}
            />
            </Flex>
            <form onSubmit={handleInputFormSubmit}>
              <InputGroup size="sm">
                <SearchBar
                  background="#F9F9FB"
                  color="#637381"
                  setSearchText={(e) => setSearchText(e.target.value)}
                  searchText={searchText}
                  borderRadius="5px"
                  borderWidth="0.5px"
                  borderColor="#637381"
                />
              </InputGroup>
            </form>
          </Flex>
           <Text
                borderRadius="5px"
                borderWidth="0.5px"
                borderColor="#637381"
                background="#F9F9FB"
                color="#637381"
                p={1}
                w={{ base: "full", md: "auto" }}
              >
                Total Subscriptions: {totalCount}
            </Text>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(value) => {
              setSelectedRowsData([]);
              setCurrentPage(value);
            }}
            totalCount={totalCount}
            setCurrentPage={setCurrentPage}
            setPageSize={setPageSize}
            pageSize={pageSize}
            setPageSizeToLocalStorage={(value) =>
              setFilterToLocalStorage(
                ENTITY_FILTER_TYPES.USERS_SUBSCRIPTION_PAGE_SIZE,
                value
              )
            }
          />
        </Flex>
        <SidebarFilter
          filters={allFiltersProps}
          handleFilterChange={handleFilterChange}
          handleApplyFilter={handleApplyFilter}
          isOpen={isOpenFilterSidebar}
          onClose={toggleSidebar}
          appliedFilters={filters}
          lsFilters={lsFilters}
          setFilters={setFilters}
          handleRemoveAllFilter={handleRemoveAllFilter}
          isCalenderFilter={true}
          enableMultiselect={true}
          multiSelectKey={["plan", "created_by", "sales_person"]}
        />
        {subscriptionData.length !== 0 ? (
          <CustomTable
            columns={visibleColumns}
            data={data}
            onSelectedAction={setSelectedAction}
            selectedRowsData={selectedRowsData}
            handleRowSelect={handleRowSelect}
            setSelectedRowsData={setSelectedRowsData}
            sortableColumns={sortableColumns}
            sortConfig={sortConfig}
            setSortConfig={setSortConfig}
            height={{base: 'calc(100vh - 210px)', md: 'calc(100vh - 80px)'}}
            menuComponent={
              <Menu
                data={Object.values(SUB_STATUS)}
                selectedAction={selectedAction}
                dataType="subscriptions"
                handleAddProperty={handleAddProperty}
                onUpdatePropertyStatus={handleMenuOptionClick}
              />
            }
          />
        ) : !loading ? (
          <NoDataFound />
        ) : null}
        <OverlayLoaderComponent isOpen={loading} />
      </Box>
      <AddPropertyModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        properties={properties}
        onSubmit={handleSubmitProperty}
      />
    </>
  );
}
